import React, { useState, useEffect, useRef, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from './../../AuthContext/AuthContext';
import {
  Box, Card, CardContent, CardMedia, Typography, IconButton,
  Button, Tooltip, Chip, CircularProgress, Tabs, Tab,
} from '@mui/material';
import {
  FavoriteBorder, Favorite, Share, Call, Chat,
  LocationOn, ChevronLeft, ChevronRight, ArrowForward,
  CheckCircle,
} from '@mui/icons-material';
import axios from 'axios';
import buildingImage from './../../../Images/house.jpeg';
import CustomSearchBar from './../Lease Module/CustomSearch';
import BottomNavbar from './BottomNavbar/BottomNavbar';
import { BASE_URL } from './../../Api/ApiUrls';
import LimitModal from '../Buy Module/LimitModal';
import SubscriptionModal from '../Buy Module/SubscriptionModal';
import AlertModal from '../Buy Module/AlertModal';
import GuestLoginModal from '../../GuestLoginModal';
import CopyFailureAlert from '../Alertpage/CopyAlert/CopyFailureAlert';
import CopySuccessAlert from '../Alertpage/CopyAlert/CopySuccessAlert';
import { NODE_API } from '../Buy Module/NodeApi';

// ─── NEW: also import the hook, and use panel="rent" ────────────────────────
import AddPropertyCards, { useAdsForPanel } from '../AddsProducts/AddPropertyCards';

// --- CUSTOM MODALS ---
import LocationModal from '../Buy Module/LocationModal';
import ShareModal from '../Buy Module/ShareModal';

const PAGE_SIZE = 20;

// ─── helpers ────────────────────────────────────────────────────────────────
const formatPrice = (price) => {
  const num = Number(String(price || '').replace(/[^0-9.]/g, ''));
  if (!num || isNaN(num) || num <= 0) return '0';
  return num.toLocaleString('en-IN');
};

const formatTotalPrice = (price) => {
  const num = Number(String(price || '').replace(/[^0-9.]/g, ''));
  if (isNaN(num) || num <= 0) return '0';
  if (num >= 10000000) return `${(num / 10000000).toFixed(2).replace(/\.?0+$/, '')}CR`;
  if (num >= 100000) return `${(num / 100000).toFixed(2).replace(/\.?0+$/, '')}L`;
  if (num >= 1000) return `${(num / 1000).toFixed(2).replace(/\.?0+$/, '')}K`;
  return num.toString();
};

const getAreaNumber = (property) => {
  if (!property) return 0;
  const raw = property.site_area || property.buildupArea || property.buildup_area || property.max_acres || property.area;
  if (raw == null) return 0;
  const n = parseFloat(String(raw).replace(/[^0-9.]/g, ''));
  return isNaN(n) ? 0 : n;
};

const getPricePerUnit = (property) => {
  const price = Number(String(property?.price || '').replace(/[^0-9.]/g, ''));
  if (!price || price <= 0) return null;

  let area = 0;
  let unit = 'sft';
  const units = String(property?.units || '').toLowerCase();

  if (property?.site_area) {
    area = parseFloat(String(property.site_area).replace(/[^0-9.]/g, '')) || 0;
  } else if (property?.max_acres) {
    area = parseFloat(String(property.max_acres).replace(/[^0-9.]/g, '')) || 0;
    if (['acre', 'acres', 'gunta', 'guntas'].includes(units)) unit = units;
  } else if (property?.area) {
    area = parseFloat(String(property.area).replace(/[^0-9.]/g, '')) || 0;
  }

  if (!area || area <= 0) return null;
  return { value: Math.round(price / area), unit };
};

// ─── Skeleton card ──────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <Box sx={{
    mb: 1.2, mx: 2, borderRadius: 3, overflow: 'hidden', boxShadow: 2,
    backgroundColor: '#fff',
    animation: 'skeletonPulse 1.5s ease-in-out infinite',
    '@keyframes skeletonPulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.45 } },
  }}>
    <Box sx={{ display: 'flex', height: 140 }}>
      <Box sx={{ width: '40%', backgroundColor: '#ddd' }} />
      <Box sx={{ width: '60%', p: 1.5 }}>
        <Box sx={{ height: 14, backgroundColor: '#e0e0e0', borderRadius: 1, mb: 1, width: '65%' }} />
        <Box sx={{ height: 12, backgroundColor: '#e0e0e0', borderRadius: 1, mb: 1, width: '45%' }} />
        <Box sx={{ height: 12, backgroundColor: '#e0e0e0', borderRadius: 1, mb: 1.5, width: '55%' }} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box sx={{ flex: 1, height: 32, backgroundColor: '#e0e0e0', borderRadius: 1 }} />
          <Box sx={{ flex: 1, height: 32, backgroundColor: '#e0e0e0', borderRadius: 1 }} />
          <Box sx={{ flex: 1, height: 32, backgroundColor: '#e0e0e0', borderRadius: 1 }} />
        </Box>
      </Box>
    </Box>
  </Box>
);

const ChipRowSkeleton = () => (
  <Box sx={{ display: 'flex', gap: 0.6, px: 0, pb: 1, overflowX: 'hidden' }}>
    {[80, 60, 100, 70, 90].map((w, i) => (
      <Box
        key={i}
        sx={{
          width: w, height: 24, borderRadius: '20px', flexShrink: 0,
          backgroundColor: '#d0d0d0',
          animation: 'skeletonPulse 1.5s ease-in-out infinite',
          '@keyframes skeletonPulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.45 } },
        }}
      />
    ))}
  </Box>
);

// ─── Scroll sentinel ────────────────────────────────────────────────────────
const ScrollSentinel = ({ onVisible, hasMore, loading }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && hasMore && !loading) onVisible(); },
      { threshold: 0.1, rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [onVisible, hasMore, loading]);

  return (
    <Box ref={ref} sx={{ py: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      {loading && <CircularProgress size={28} sx={{ color: '#8B7355' }} />}
      {!loading && !hasMore && (
        <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
          ✓ All properties loaded
        </Typography>
      )}
    </Box>
  );
};

// ─── Parse raw API row → card shape ────────────────────────────────────────
const parseProperty = (item) => {
  const parseCoord = (coord) => {
    if (!coord) return null;
    return parseFloat(String(coord).replace(/"/g, '').replace(':', '.'));
  };

  let images = [buildingImage];
  if (item.property_images?.length > 0) {
    images = item.property_images.map(img => {
      if (typeof img === 'string') return img.startsWith('http') ? img : `${BASE_URL}${img}`;
      if (img.image) return img.image.startsWith('http') ? img.image : `${BASE_URL}/${img.image.replace(/^\/+/, '')}`;
      if (img.url) return img.url.startsWith('http') ? img.url : `${BASE_URL}${img.url}`;
      if (img.path) return img.path.startsWith('http') ? img.path : `${BASE_URL}${img.path}`;
      return buildingImage;
    });
  } else if (item.images?.length > 0) {
    images = item.images.map(img => typeof img === 'string' && img.startsWith('http') ? img : `${BASE_URL}${img}`);
  }

  return {
    id: String(item.property_id),
    title: item.category_name || item.property_type || 'Property',
    property_name: item.property_name,
    location: item.location || 'Not specified',
    date: item.created_at?.split('T')[0],
    facing: item.facing?.replace(/"/g, '') || 'N/A',
    city_town: item.city_town || 'Unknown',
    price: Number(item.price) || 0,
    possession: item.possession || 'Not specified',
    createdAt: item.created_at,
    fullData: item,
    length: item.length,
    width: item.width,
    listedBy: item.posted_by?.replace(/"/g, '') || 'Agent',
    mobile_no: item.mobile_no,
    units: item.units || '',
    category_id: item.category_id,
    category_name: item.category_name || '',
    images,
    site_area: item.site_area,
    area: item.site_area ? `${item.site_area} ${['ft', 'm', 'cm', 'in'].includes(item.units) ? `Sq ${item.units}` : item.units || 'Sq ft'}` : 0,
    max_acres: item.max_acres ? `${item.max_acres} ${item.units || 'acres'}` : 'N/A',
    posted_by: item.posted_by?.replace(/"/g, '') || 'Agent',
    lat: parseCoord(item.lat),
    long: parseCoord(item.long),
     distance_km: item.distance_km != null ? Number(item.distance_km) : null,   // ← add
    owner: item.user_id,
    propertyData: item,
    property_images: item.property_images,
    property_type: item.property_type || '',
  };
};

// ─── Debounce hook ──────────────────────────────────────────────────────────
const useDebounce = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const readUserCoords = () => {
  const lat = parseFloat(localStorage.getItem('userLatitude'));
  const lng = parseFloat(localStorage.getItem('userLongitude'));
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat: Math.round(lat * 1000) / 1000, lng: Math.round(lng * 1000) / 1000 };
};

const useUserCoords = () => {
  const [coords, setCoords] = useState(readUserCoords);
  useEffect(() => {
    const refresh = () =>
      setCoords(prev => {
        const next = readUserCoords();
        const same = prev?.lat === next?.lat && prev?.lng === next?.lng;
        return same ? prev : next; // keep reference stable → no needless refetch
      });
    window.addEventListener('storage', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, []);
  return coords;
};

// ─── Generic paginated fetch hook ───────────────────────────────────────────
const usePaginatedFetch = (getUrl, deps, enabled = true) => {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const fetchingRef = useRef(false);
  const [isFetching, setIsFetching] = useState(false);

  const setFetching = (val) => {
    fetchingRef.current = val;
    setIsFetching(val);
  };

  const abortRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setItems([]);
    setCursor(null);
    setHasMore(false);
    setInitialLoading(true);
    setFetching(true);

    fetch(getUrl(null), { signal: ctrl.signal })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(json => {
        setItems((json.data || []).map(i => parseProperty(i)));
        setCursor(json.next_cursor ?? null);
        setHasMore(json.has_more ?? false);
      })
      .catch(err => { if (err.name !== 'AbortError') console.error('fetch error:', err); })
      .finally(() => {
        setInitialLoading(false);
        setFetching(false);
      });

    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, enabled]);

  const loadMore = useCallback(() => {
    if (loading || !hasMore || !cursor) return;
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    fetch(getUrl(cursor), { signal: ctrl.signal })
      .then(r => r.json())
      .then(json => {
        setItems(prev => [...prev, ...(json.data || []).map(i => parseProperty(i))]);
        setCursor(json.next_cursor ?? null);
        setHasMore(json.has_more ?? false);
      })
      .catch(err => { if (err.name !== 'AbortError') console.error('loadMore error:', err); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hasMore, cursor, ...deps]);

  return { items, hasMore, loading, initialLoading, isFetching, fetchingRef, loadMore };
};

const useAllRentProperties = ({ searchQuery, priceFilter, coords, enabled }) => {
  const getUrl = useCallback((cursorVal) => {
    const p = new URLSearchParams({ limit: PAGE_SIZE });
    if (cursorVal) p.set('cursor', cursorVal);
    if (coords) { p.set('lat', coords.lat); p.set('lng', coords.lng); }
    if (searchQuery.trim()) p.set('search', searchQuery.trim());
    if (priceFilter?.min != null) p.set('min_price', priceFilter.min);
    if (priceFilter?.max != null) p.set('max_price', priceFilter.max);
    return `${NODE_API}/api/rent/non-admin/?${p}`;
  }, [searchQuery, priceFilter, coords]);

  return usePaginatedFetch(
    getUrl,
    [searchQuery, JSON.stringify(priceFilter), coords?.lat, coords?.lng],
    enabled
  );
};

const useRentByCategory = ({ selectedCategoryId, searchQuery, priceFilter, coords, enabled }) => {
  const getUrl = useCallback((cursorVal) => {
    const p = new URLSearchParams({ limit: PAGE_SIZE });
    if (cursorVal) p.set('cursor', cursorVal);
    if (coords) { p.set('lat', coords.lat); p.set('lng', coords.lng); }
    if (searchQuery?.trim()) p.set('search', searchQuery.trim());
    if (priceFilter?.min != null) p.set('min_price', priceFilter.min);
    if (priceFilter?.max != null) p.set('max_price', priceFilter.max);
    return `${NODE_API}/api/rent/by-category/${selectedCategoryId}?${p}`;
  }, [selectedCategoryId, searchQuery, priceFilter, coords]);

  return usePaginatedFetch(
    getUrl,
    [selectedCategoryId, searchQuery, JSON.stringify(priceFilter), coords?.lat, coords?.lng],
    enabled && selectedCategoryId != null
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────
const RentPropertyCard = () => {
  const navigate = useNavigate();
  const { userId, isGuest } = useContext(AuthContext);

  // UI state
  const [activeTab, setActiveTab] = useState('rent');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [priceFilter, setPriceFilter] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState({});

  const debouncedSearch = useDebounce(searchQuery, 400);
  const isTyping = searchQuery !== debouncedSearch;

  // Data state
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [allFetchedProperties, setAllFetchedProperties] = useState([]);

  // Auth / subscription
  const [savedProperties, setSavedProperties] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [saving, setSaving] = useState({});
  const [locationLoading, setLocationLoading] = useState({});
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [userPlan, setUserPlan] = useState(null);
  const [userActivities, setUserActivities] = useState([]);

  // Modals
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [featureRequested, setFeatureRequested] = useState('');
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [currentPropertyId, setCurrentPropertyId] = useState(null);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  // --- MODAL STATES ---
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [currentPropertyData, setCurrentPropertyData] = useState(null);

  // ─── Ads hook (panel="rent", shared, fetched once) ──────────────────────
  const { ads: rentAds, loading: adsLoading } = useAdsForPanel('rent');

 const coords = useUserCoords();

const allProps = useAllRentProperties({
  searchQuery: debouncedSearch,
  priceFilter,
  coords,
  enabled: activeTab === 'rent' && !selectedCategory,
});

const categoryProps = useRentByCategory({
  selectedCategoryId: selectedCategory?.category_id ?? null,
  searchQuery: debouncedSearch,
  priceFilter,
  coords,
  enabled: activeTab === 'rent' && !!selectedCategory,
});

  const {
    items: rentProperties,
    hasMore: rentHasMore,
    loading: rentListLoading,
    initialLoading: rentInitialLoading,
    isFetching: rentIsFetching,
    fetchingRef: rentFetchingRef,
    loadMore: rentLoadMore,
  } = selectedCategory ? categoryProps : allProps;

  const showSkeleton = isTyping || rentIsFetching || rentFetchingRef?.current;

  useEffect(() => {
    if (!rentProperties.length) return;
    setAllFetchedProperties(prev => {
      const existingIds = new Set(prev.map(p => p.id));
      const newOnes = rentProperties.filter(p => !existingIds.has(p.id));
      return newOnes.length ? [...prev, ...newOnes] : prev;
    });
  }, [rentProperties]);

  // Memoized
  const accessedProperties = useMemo(() => {
    return allFetchedProperties.filter(property =>
      userActivities.some(a => String(a.property_id) === String(property.id) && a.activity_as === 'Tenant')
    );
  }, [allFetchedProperties, userActivities]);

  const properties = useMemo(() => activeTab === 'accessed' ? accessedProperties : rentProperties, [activeTab, accessedProperties, rentProperties]);
  const hasMore = useMemo(() => activeTab === 'accessed' ? false : rentHasMore, [activeTab, rentHasMore]);
  const listLoading = useMemo(() => activeTab === 'accessed' ? false : rentListLoading, [activeTab, rentListLoading]);
  const initialLoading = useMemo(() => activeTab === 'accessed' ? false : rentInitialLoading, [activeTab, rentInitialLoading]);
  const loadMore = useMemo(() => activeTab === 'accessed' ? () => {} : rentLoadMore, [activeTab, rentLoadMore]);

  // Bootstrap
  useEffect(() => {
    setCategoriesLoading(true);
    fetch(`${NODE_API}/api/rent/categories/`)
      .then(r => r.json())
      .then(data => { setCategories(data); setCategoriesLoading(false); })
      .catch(() => setCategoriesLoading(false));
  }, []);

  useEffect(() => {
    if (!userId) return;

    axios.get(`${BASE_URL}/Get-user-plan/${userId}/`)
      .then(r => {
        const subs = r.data;
        const tenantSub = Array.isArray(subs) ? subs.find(s => s.user_type === 'Tenant') : null;
        setIsSubscribed(!!tenantSub);
        setUserPlan(tenantSub || null);
      })
      .catch(() => { setIsSubscribed(false); setUserPlan(null); });

    axios.get(`${BASE_URL}/get-user-cart/${userId}/`)
      .then(r => { setCartItems(r.data); setSavedProperties(r.data.map(i => String(i.property_id))); })
      .catch(e => console.error('Cart error:', e));

    fetchUserActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    setCurrentImageIndex(prev => {
      let changed = false;
      const next = { ...prev };
      properties.forEach(p => {
        if (!(p.id in next)) { next[p.id] = 0; changed = true; }
      });
      return changed ? next : prev;
    });
  }, [properties]);

  // Helpers
  const requireLogin = useCallback((e, cb) => {
    e.stopPropagation();
    if (isGuest) { setShowGuestModal(true); return; }
    cb();
  }, [isGuest]);

  const fetchUserActivities = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${BASE_URL}/activities/`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const all = await res.json();
      setUserActivities(all.filter(a => String(a.user_id) === String(userId)));
    } catch (e) {
      console.error('Activities error:', e);
      setUserActivities([]);
    }
  }, [userId]);

  const isSaved = useCallback((property) => savedProperties.includes(String(property.id)), [savedProperties]);

  const addToCart = useCallback(async (propertyId) => {
    const r = await axios.post(`${BASE_URL}/user-cart/`, { user_id: userId, property_id: propertyId });
    return r.data;
  }, [userId]);

  const removeFromCart = useCallback(async (cartId) => axios.delete(`${BASE_URL}/user-cart/${cartId}/`), []);

  const toggleSave = useCallback(async (property) => {
    const pid = String(property.id);
    setSaving(prev => ({ ...prev, [pid]: true }));
    try {
      if (isSaved(property)) {
        const item = cartItems.find(i => String(i.property_id) === pid);
        if (item) await removeFromCart(item.cart_id);
        setSavedProperties(prev => prev.filter(id => id !== pid));
        setCartItems(prev => prev.filter(i => String(i.property_id) !== pid));
      } else {
        const newItem = await addToCart(pid);
        setSavedProperties(prev => [...prev, pid]);
        setCartItems(prev => [...prev, newItem]);
      }
    } catch (e) {
      console.error('Save toggle error:', e);
      setAlertMessage(isSaved(property) ? 'Failed to remove from wishlist' : 'Failed to add to wishlist');
      setShowFailure(true);
    } finally {
      setSaving(prev => ({ ...prev, [pid]: false }));
    }
  }, [isSaved, cartItems, addToCart, removeFromCart]);

  const findProperty = useCallback((propertyId) =>
    properties.find(p => p.id === propertyId) || allFetchedProperties.find(p => p.id === propertyId),
  [properties, allFetchedProperties]);

  const checkPropertyLimit = useCallback((propertyId) => {
    if (!isSubscribed) {
      setFeatureRequested('Property Access');
      setShowSubscribeModal(true);
      return false;
    }
    if (userPlan?.no_of_properties_unlimited) return true;

    const alreadyAccessed = userActivities.some(a => a.property_id === propertyId && a.activity_as === 'Tenant');
    if (alreadyAccessed) return true;

    const uniqueProps = new Set(userActivities.filter(a => a.activity_as === 'Tenant').map(a => a.property_id));
    if (uniqueProps.size >= (userPlan?.no_of_properties || 10)) {
      setShowLimitModal(true);
      return false;
    }
    return true;
  }, [isSubscribed, userPlan, userActivities]);

  const logActivity = useCallback(async (propertyId, activityType) => {
    const exists = userActivities.find(a => a.property_id === propertyId && a.activity_as === 'Tenant');
    if (exists) return false;

    const property = findProperty(propertyId);
    if (!property) return false;

    try {
      const res = await fetch(`${BASE_URL}/activities/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          property_id: propertyId,
          activity_type: activityType,
          property_by: property.owner,
          activity_as: 'Tenant',
        }),
      });
      if (!res.ok) throw new Error('Failed to log activity');
      return await res.json();
    } catch (e) { console.error('logActivity error:', e); return false; }
  }, [userId, userActivities, findProperty]);

  const handleAction = useCallback(async (e, propertyId, actionName, actionCallback, activityType) => {
    e.stopPropagation();
    if (!isSubscribed) { setFeatureRequested(actionName); setShowSubscribeModal(true); return; }

    const alreadyAccessed = userActivities.some(a => a.property_id === propertyId && a.activity_as === 'Tenant');
    if (!alreadyAccessed) {
      const ok = checkPropertyLimit(propertyId);
      if (!ok) return;
    }

    setPendingAction(() => async () => {
      try {
        await actionCallback();
        if (activityType && !alreadyAccessed) {
          const result = await logActivity(propertyId, activityType);
          if (result) {
            setUserActivities(prev => [...prev, {
              user_id: userId,
              property_id: propertyId,
              activity_type: activityType,
              property_by: findProperty(propertyId)?.owner,
              activity_as: 'Tenant',
              created_at: new Date().toISOString(),
            }]);
          }
        }
      } catch (err) {
        console.error(`${actionName} error:`, err);
      }
    });
    setCurrentPropertyId(propertyId);
    setShowAlertModal(true);
  }, [isSubscribed, userActivities, checkPropertyLimit, logActivity, userId, findProperty]);

  // Action handlers
  const handleCallClick = useCallback((e, property) => {
    return handleAction(e, property.id, 'Call', async () => {
      try {
        await navigator.clipboard.writeText(property.mobile_no);
        setAlertMessage(`Copied: ${property.mobile_no}`);
        setShowSuccess(true); setShowFailure(false);
      } catch {
        setAlertMessage('Failed to copy number');
        setShowFailure(true); setShowSuccess(false);
      }
    }, 'Call');
  }, [handleAction]);

  const handleMessageClick = useCallback((e, property) => {
    return handleAction(e, property.id, 'Message', async () => {
      navigate('/chat', { state: { property: property.id } });
    }, 'Call');
  }, [handleAction, navigate]);

  const handleShareClick = useCallback((e, property) => {
    e.stopPropagation();
    setCurrentPropertyData(property);
    setShowShareModal(true);
  }, []);

  const handleLocationClick = useCallback((e, property) => {
    e.stopPropagation();
    setCurrentPropertyData(property);
    setShowLocationModal(true);
  }, []);

  const openGoogleMaps = () => {
    if (!currentPropertyData) return;
    setLocationLoading(prev => ({ ...prev, [currentPropertyData.id]: true }));

    const fallback = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(currentPropertyData.location)}`;
    const open = (url) => {
      setLocationLoading(prev => ({ ...prev, [currentPropertyData.id]: false }));
      window.open(url, '_blank', 'noopener,noreferrer');
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          const dest = currentPropertyData.lat && currentPropertyData.long
            ? `https://www.google.com/maps/dir/?api=1&origin=${coords.latitude},${coords.longitude}&destination=${currentPropertyData.lat},${currentPropertyData.long}&travelmode=driving`
            : fallback;
          open(dest);
        },
        () => open(fallback),
        { timeout: 10000 }
      );
    } else { open(fallback); }
  };

  const handleWishlistClick = useCallback((e, property) => {
    e.stopPropagation();
    return handleAction(e, property.id, 'Liked', async () => {
      await toggleSave(property);
      setShowSuccessModal(true);
    }, 'Liked');
  }, [handleAction, toggleSave]);

  const handleNextImage = useCallback((propertyId, e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => {
      const p = findProperty(propertyId);
      return { ...prev, [propertyId]: (prev[propertyId] + 1) % p.images.length };
    });
  }, [findProperty]);

  const handlePrevImage = useCallback((propertyId, e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => {
      const p = findProperty(propertyId);
      return { ...prev, [propertyId]: (prev[propertyId] - 1 + p.images.length) % p.images.length };
    });
  }, [findProperty]);

  // ── Card renderer ──
  const renderCard = useCallback((property) => {
    const isAccessed = userActivities.some(a => String(a.property_id) === String(property.id) && a.activity_as === 'Tenant');
    const imgIdx = currentImageIndex[property.id] || 0;
    const pricePerUnit = getPricePerUnit(property);
    const totalArea = getAreaNumber(property);

    return (
      <Card
        key={property.id}
        sx={{
          mb: 1.2, mx: 2, borderRadius: 2, boxShadow: 2, display: 'flex', flexDirection: 'row',
          width: 'auto', height: 170, overflow: 'hidden', transition: 'transform 0.2s ease-in-out',
          '&:hover': { transform: 'scale(1.015)', boxShadow: 4 }, bgcolor: '#fff', flexShrink: 0,
        }}
        onClick={async (e) => {
          if (e.target.closest('button') || e.target.closest('svg')) return;
          if (!isSubscribed) { setFeatureRequested('View Details'); setShowSubscribeModal(true); return; }
          const alreadyAccessed = userActivities.some(a => a.property_id === property.id && a.activity_as === 'Tenant');
          if (!alreadyAccessed) {
            const ok = checkPropertyLimit(property.id);
            if (!ok) return;
            try { await logActivity(property.id, 'Description'); } catch { }
          }
          navigate(`/rent-description/${property.id}`, {
            state: { propertyId: property.id, property: property.propertyData },
          });
        }}
      >
        {/* ── LEFT: Image ── */}
        <Box sx={{ position: 'relative', width: '38%', minWidth: '38%', height: '100%', overflow: 'hidden' }}>
          <CardMedia component="img" image={property.images[imgIdx]} alt="Property" sx={{ width: '100%', height: '100%', objectFit: 'cover', borderTopLeftRadius: 8, borderBottomLeftRadius: 8 }} />

          {property.images.length > 1 && (
            <>
              <IconButton sx={{ position: 'absolute', top: '50%', left: 4, transform: 'translateY(-50%)', backgroundColor: 'rgba(255,255,255,0.6)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' }, p: 0.3, width: 22, height: 22 }} onClick={e => handlePrevImage(property.id, e)}>
                <ChevronLeft fontSize="small" />
              </IconButton>
              <IconButton sx={{ position: 'absolute', top: '50%', right: 4, transform: 'translateY(-50%)', backgroundColor: 'rgba(255,255,255,0.6)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' }, p: 0.3, width: 22, height: 22 }} onClick={e => handleNextImage(property.id, e)}>
                <ChevronRight fontSize="small" />
              </IconButton>
            </>
          )}

          {property.images.length > 1 && (
            <Box sx={{ position: 'absolute', bottom: 6, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '4px' }}>
              {property.images.map((_, i) => (
                <Box key={i} onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(prev => ({ ...prev, [property.id]: i })); }} sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: imgIdx === i ? '#1976d2' : '#ccc', cursor: 'pointer' }} />
              ))}
            </Box>
          )}

          <Tooltip title={isSaved(property) ? 'Remove from Wishlist' : 'Add to Wishlist'}>
            <IconButton
              onClick={(e) => requireLogin(e, () => handleWishlistClick(e, property))}
              disabled={saving[String(property.id)]}
              sx={{ position: 'absolute', top: 7, right: 3, bgcolor: 'white', boxShadow: 1, p: 0.5, width: 24, height: 24, '&:hover': { bgcolor: '#f5f5f5' } }}
            >
              {saving[String(property.id)] ? <CircularProgress size={16} /> : isSaved(property) ? <Favorite color="error" fontSize="small" /> : <FavoriteBorder fontSize="small" />}
            </IconButton>
          </Tooltip>

          {isAccessed && (
            <Box sx={{ position: 'absolute', top: 6, left: 6, bgcolor: 'rgba(46, 125, 50, 0.9)', borderRadius: '50%', p: 0.3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle sx={{ fontSize: 16, color: 'white' }} />
            </Box>
          )}
        </Box>

        {/* ── RIGHT: Content ── */}
        <CardContent sx={{ width: '62%', p: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: 170, boxSizing: 'border-box', '&:last-child': { pb: 1 } }}>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.2 }}>
            <Typography fontWeight="bold" noWrap sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.75rem', lineHeight: 1.2, color: '#2C1810', mr: 0.3 }}>
              {property.title}{property.property_name ? ` - ${property.property_name}` : ''}
            </Typography>

            <IconButton onClick={(e) => requireLogin(e, () => handleShareClick(e, property))} size="small" sx={{ width: 25, height: 25, p: 0, flexShrink: 0, bgcolor: '#FFFFFF', boxShadow: '0 2px 4px rgba(0,0,0,0.08)', '&:hover': { bgcolor: '#F5EDE4' } }}>
              <Share sx={{ fontSize: 14, color: '#8D6E63' }} />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.1, mb: 0.2 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.5rem', color: '#888', lineHeight: 1.1 }}>Price / Per {pricePerUnit?.unit || 'sft'}</Typography>
              <Typography fontWeight="bold" sx={{ fontSize: '0.68rem', color: '#8B4F16', lineHeight: 1.2, fontWeight: 900 }}>
                ₹{pricePerUnit ? formatPrice(pricePerUnit.value) : 'NA'}
                <Typography component="span" sx={{ fontSize: '0.5rem', color: '#888', fontWeight: 400 }}> /{pricePerUnit?.unit || 'sft'}</Typography>
              </Typography>
            </Box>
            <Box sx={{ width: '1px', height: 26, bgcolor: '#e0e0e0', mx: 0.6 }} />
            <Box sx={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
              <Typography sx={{ fontSize: '0.5rem', color: '#888', lineHeight: 1.1 }}>Total Price</Typography>
              <Typography fontWeight="bold" sx={{ fontSize: '1rem', lineHeight: 1.2, color: '#1F1F1F' }}>₹{formatTotalPrice(property.price)}</Typography>
            </Box>
          </Box>

          <Box 
          // onClick={(e) => requireLogin(e, () => handleLocationClick(e, property))} 
          role="button" tabIndex={0} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.1, mb: 0.1, cursor: 'pointer', borderRadius: 1, px: 0.3, transition: 'background-color 0.15s', '&:hover': { backgroundColor: 'rgba(201,138,44,0.10)' }, '&:active': { backgroundColor: 'rgba(201,138,44,0.18)' } }}>
            {locationLoading[property.id] ? <CircularProgress size={14} sx={{ mt: 0.1, flexShrink: 0 }} /> : <LocationOn sx={{ fontSize: '0.9rem', color: '#8B4F16', mt: 0.1, flexShrink: 0 }} />}
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
              <Typography sx={{ minWidth: 0, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', textOverflow: 'ellipsis', whiteSpace: 'normal', color: '#5D4037', fontSize: { xs: '0.62rem', sm: '0.72rem' }, lineHeight: 1.2, fontWeight: 500 }}>
                {property.location || 'Location not available'}
              </Typography>
              {property.distance_km != null && property.distance_km < 99999 && (
  <Typography sx={{ fontSize: '0.58rem', color: '#8B4F16', fontWeight: 700, lineHeight: 1.2 }}>
    {property.distance_km < 1
      ? `${Math.round(property.distance_km * 1000)} m away`
      : `${property.distance_km.toFixed(1)} km away`}
  </Typography>
)}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.2, mb: 0.2, border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden', minHeight: 30 }}>
            <Box sx={{ flex: 1.2, minWidth: 0, textAlign: 'center', p: 0.4, borderRight: '1px solid #e0e0e0' }}>
              <Typography fontWeight="bold" noWrap sx={{ fontSize: '0.62rem', lineHeight: 1.1, fontWeight: 900 }}>{property.facing ? String(property.facing).split('-')[0] : 'N/A'}</Typography>
              <Typography noWrap sx={{ fontSize: '0.62rem', lineHeight: 1.1, color: '#666', fontWeight: 900 }}>{String(property.facing || '').split('-')[1] || ''}</Typography>
            </Box>
            <Box sx={{ flex: 1.4, minWidth: 0, textAlign: 'center', p: 0.4, borderRight: '1px solid #e0e0e0' }}>
              <Typography fontWeight="bold" noWrap sx={{ fontSize: '0.62rem', lineHeight: 1.1, maxWidth: '60px', mx: 'auto', fontWeight: 900 }}>{totalArea > 0 ? totalArea : 'N/A'}</Typography>
              {property.units && <Typography noWrap sx={{ fontSize: '0.5rem', lineHeight: 1.1, color: '#666' }}>{['ft', 'm', 'cm', 'in'].includes(String(property.units).toLowerCase()) && !String(property.units).toLowerCase().startsWith('sq.') ? `sq.${property.units}` : property.units}</Typography>}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', p: 0.3 }}>
              <Typography fontWeight="bold" noWrap sx={{ fontSize: '0.62rem', lineHeight: 1.1, fontWeight: 900 }}>{property.posted_by || 'Agent'}</Typography>
            </Box>
          </Box>

          <Button
            variant="contained" size="small" fullWidth
            onClick={(e) => {
              e.stopPropagation();
              (async () => {
                if (!isSubscribed) { setFeatureRequested('View Details'); setShowSubscribeModal(true); return; }
                const alreadyAccessed = userActivities.some(a => a.property_id === property.id && a.activity_as === 'Tenant');
                if (!alreadyAccessed) {
                  const ok = checkPropertyLimit(property.id);
                  if (!ok) return;
                  try { await logActivity(property.id, 'Description'); } catch { }
                }
                navigate(`/rent-description/${property.id}`, { state: { propertyId: property.id, property: property.propertyData } });
              })();
            }}
            sx={{ mt: 0.2, py: 0.18, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#8B4F16', color: '#fff', borderRadius: '2px', '&:hover': { bgcolor: '#f5f5f5' }, textTransform: 'none', minHeight: 22, display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1.5 }}
          >
            View Details <ArrowForward sx={{ fontSize: '0.9rem' }} />
          </Button>
        </CardContent>
      </Card>
    );
  }, [userActivities, currentImageIndex, isSubscribed, checkPropertyLimit, logActivity, navigate, requireLogin, handleWishlistClick, handleShareClick, handleLocationClick, handleMessageClick, handleCallClick, saving, isSaved, handlePrevImage, handleNextImage]);

  // ─── Properties section — now inserts ads after EVERY 2 cards ────────────
  const renderPropertiesSection = useMemo(() => {
    if (initialLoading) return <Box sx={{ mt: 1 }}>{[1, 2, 3].map(i => <SkeletonCard key={i} />)}</Box>;

    if (activeTab === 'rent' && showSkeleton) {
      return (
        <Box>
          {isTyping && <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 1.5 }}><CircularProgress size={16} sx={{ color: '#8B7355' }} /><Typography variant="caption" sx={{ color: '#888' }}>Searching...</Typography></Box>}
          <Box sx={{ mt: isTyping ? 0.5 : 1 }}>{[1, 2, 3].map(i => <SkeletonCard key={i} />)}</Box>
        </Box>
      );
    }

    if (properties.length === 0) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 200, gap: 1, px: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {activeTab === 'accessed' ? "You haven't accessed any properties yet" : selectedCategory ? `No properties found for "${selectedCategory.category}"` : debouncedSearch ? `No results for "${debouncedSearch}"` : 'No properties available right now.'}
          </Typography>
        </Box>
      );
    }

    return (
      <>
        {properties.map((property, index) => {
          const cardNumber = index + 1; // 1-based

          // Insert an ad block AFTER every 2 property cards (rent tab only)
          let adBlock = null;
          if (activeTab === 'rent' && cardNumber % 2 === 0) {
            const adOffset = (cardNumber / 2 - 1) * 3;
            // Render if first block (even while loading) OR there are still ads left at this offset
            if (adOffset === 0 || adOffset < rentAds.length) {
              adBlock = (
                <AddPropertyCards
                  key={`ads-${adOffset}`}
                  ads={rentAds}
                  offset={adOffset}
                  loading={adsLoading}
                />
              );
            }
          }

          return (
            <React.Fragment key={property.id}>
              {renderCard(property)}
              {adBlock}
            </React.Fragment>
          );
        })}
        <ScrollSentinel onVisible={loadMore} hasMore={hasMore} loading={listLoading} />
      </>
    );
  }, [
    initialLoading, activeTab, showSkeleton, isTyping, properties,
    selectedCategory, debouncedSearch, renderCard, loadMore,
    hasMore, listLoading,
    rentAds, adsLoading,
  ]);

  return (
    <Box sx={{ backgroundColor: 'rgb(239, 231, 221)', minHeight: '120vh' }}>
      <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1200, backgroundColor: 'rgb(239, 231, 221)' }}>
        <Box sx={{ px: 1, pt: 1, pb: 0.25 }}>
          <CustomSearchBar value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onPriceFilterChange={setPriceFilter} />
        </Box>

        <Box sx={{ px: 2, pt: 0, pb: 0.5 }}>
          <Tabs value={activeTab} onChange={(e, newValue) => { setActiveTab(newValue); if (newValue === 'accessed') setSelectedCategory(null); }} sx={{ minHeight: 36, '& .MuiTabs-indicator': { backgroundColor: '#B66A18', height: 3 } }}>
            <Tab label="Rent Properties" value="rent" sx={{ textTransform: 'none', fontWeight: 'bold', minWidth: 0, mr: 2, minHeight: 36, py: 0.5, color: 'text.secondary', '&.Mui-selected': { color: '#8B4F16' } }} />
            <Tab label="Accessed Properties" value="accessed" sx={{ textTransform: 'none', fontWeight: 'bold', minWidth: 0, minHeight: 36, py: 0.5, color: 'text.secondary', '&.Mui-selected': { color: '#8B4F16' } }} />
          </Tabs>
        </Box>

        {activeTab === 'rent' && (
          <Box sx={{ px: 2, pt: 0, pb: 0.5 }}>
            {categoriesLoading ? <ChipRowSkeleton /> : (
              <Box sx={{ display: 'flex', gap: 0.6, overflowX: 'auto', flexWrap: 'nowrap', pb: 0.5, '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                {categories.map((cat) => {
                  const isActive = selectedCategory?.category_id === cat.category_id;
                  return (
                    <Chip key={cat.category_id} label={cat.category} onClick={() => setSelectedCategory(prev => prev?.category_id === cat.category_id ? null : cat)} sx={{ flexShrink: 0, height: 24, borderRadius: '20px', fontWeight: 'bold', fontSize: '13px', bgcolor: isActive ? '#8B4F16' : 'transparent', color: isActive ? '#fff' : '#000', border: '1px solid black', transition: 'all 0.2s ease', cursor: 'pointer', '&:hover': { bgcolor: isActive ? '#333' : '#e0e0e0', transform: 'scale(1.02)' }, '& .MuiChip-label': { paddingLeft: '6px', paddingRight: '6px', lineHeight: 1 } }} />
                  );
                })}
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ pb: 8, pt: activeTab === 'rent' ? { xs: '115px', sm: '155px' } : { xs: '100px', sm: '92px' } }}>
        {renderPropertiesSection}
      </Box>

      {/* --- MODALS --- */}
      <GuestLoginModal open={showGuestModal} onClose={() => setShowGuestModal(false)} />

      <AlertModal
        open={showAlertModal}
        onClose={() => setShowAlertModal(false)}
        onConfirm={async () => { setShowAlertModal(false); if (pendingAction) await pendingAction(); }}
        title="Confirm Action"
        message="Are you sure you want to perform this action on this property?"
      />

      <AlertModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Added to Wishlist"
        message="This property has been added to your Wishlist."
        isSuccess={true}
      />

      <SubscriptionModal open={showSubscribeModal} onClose={() => setShowSubscribeModal(false)} onSubscribe={() => { setShowSubscribeModal(false); navigate('/user-subscription'); }} featureRequested={featureRequested} />

      <LimitModal open={showLimitModal} onClose={() => setShowLimitModal(false)} userPlan={userPlan} />

      <LocationModal
        open={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        onOpenMap={openGoogleMaps}
        propertyLocation={currentPropertyData?.location}
      />

      <ShareModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        ownerName={currentPropertyData?.posted_by || currentPropertyData?.owner}
        mobileNo={currentPropertyData?.mobile_no}
      />

      {showSuccess && <CopySuccessAlert message={alertMessage} onClose={() => { setShowSuccess(false); setAlertMessage(''); }} />}
      {showFailure && <CopyFailureAlert message={alertMessage} onClose={() => { setShowFailure(false); setAlertMessage(''); }} />}

      <BottomNavbar />
    </Box>
  );
};

export default RentPropertyCard;